import { Outlet, createRootRoute, useRouter, HeadContent, Scripts } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
// Preloaded by URL so the hashed filenames come from the build. Without this the
// latin faces arrive after first paint and `font-display: swap` reflows the page —
// worth 0.07 of CLS on a throttled phone, which is most of the budget.
import serifLatin from "@fontsource-variable/newsreader/files/newsreader-latin-wght-normal.woff2?url";
import sansLatin from "@fontsource-variable/public-sans/files/public-sans-latin-wght-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { NotFoundPage } from "@/components/not-found";
import { PrintPrep } from "@/components/print-prep";
import { websiteJsonLd, organizationJsonLd } from "@/lib/jsonld";
import { THEME_INIT_SCRIPT } from "@/lib/theme";
import { absUrl } from "@/lib/site";

// The fallback share image for any page that doesn't set its own: a static PNG of the
// real map. Pages that do set one get a PNG from /api/og; if that renderer ever fails it
// redirects here, so a share preview is always a raster image.
const DEFAULT_OG = absUrl("/og-default.png");

function NotFoundComponent() {
  return <NotFoundPage />;
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  // No console.error here: it fires on every render of the boundary and shows up in
  // a visitor's console for a failure they cannot act on. The error still goes to
  // the reporter below, and the SSR path logs it server-side in src/server.ts.
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6 sm:py-28">
      <p className="label-caps text-rust-ink">Something went wrong</p>
      <h1 className="mt-3 font-display text-[2.4rem] font-medium leading-[1.05] tracking-[-0.02em] text-foreground sm:text-[3rem]">
        This page didn&rsquo;t load.
      </h1>
      <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">
        The fault is on our end. Try again, or go back to the home page.
      </p>
      <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="inline-flex rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary-dark"
        >
          Try again
        </button>
        <a href="/" className="link text-sm font-semibold">
          Go to the home page
        </a>
      </div>
    </div>
  );
}

// GA4 is opt-in via env so forks and local dev never report into production analytics.
// Unset => no tag script is injected at all (a genuine no-op, not a silent send).
const GA_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID ?? "").trim();

// Offline shell (public/sw.js): caches the tick-removal steps and the pages people
// need with no signal. Production only — in dev a service worker just fights HMR.
const SERVICE_WORKER_SCRIPT = import.meta.env.PROD
  ? `if('serviceWorker' in navigator){addEventListener('load',function(){navigator.serviceWorker.register('/sw.js').catch(function(){})})}`
  : "";
const analyticsScripts = GA_ID
  ? [
      { src: `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`, async: true },
      {
        children: `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${GA_ID}');`,
      },
    ]
  : [];

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Lone Star Tick Map | Alpha-Gal Risk Near You" },
      {
        name: "description",
        content:
          "Are Lone Star ticks reported where you live? A free field guide: where the tick lives, what to do after a bite, and how to avoid alpha-gal syndrome.",
      },
      {
        name: "google-site-verification",
        content: "UOLHC7jFXLvjjllk0TpX5bgt-g-864aQe_vGX9t0u7g",
      },
      { name: "author", content: "LoneStarMap" },
      { name: "theme-color", content: "#f5f2ea", media: "(prefers-color-scheme: light)" },
      { name: "theme-color", content: "#131c17", media: "(prefers-color-scheme: dark)" },
      { property: "og:site_name", content: "LoneStarMap" },
      { property: "og:type", content: "website" },
      {
        property: "og:title",
        content: "Lone Star Tick Map | Alpha-Gal Risk Near You",
      },
      {
        property: "og:description",
        content:
          "Are Lone Star ticks reported where you live? A free field guide: where the tick lives, what to do after a bite, and how to avoid alpha-gal syndrome.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: "Lone Star Tick Map | Alpha-Gal Risk Near You",
      },
      {
        name: "twitter:description",
        content:
          "Are Lone Star ticks reported where you live? A free field guide: where the tick lives, what to do after a bite, and how to avoid alpha-gal syndrome.",
      },

      {
        property: "og:image",
        content: DEFAULT_OG,
      },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: DEFAULT_OG },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: sansLatin,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: serifLatin,
        crossOrigin: "anonymous",
      },
      // No <link> to the 98KB favicon.ico: every browser in use supports a PNG icon,
      // and the .ico stays on disk for anything that probes /favicon.ico directly.
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      // Fonts are self-hosted via src/styles.css — no third-party stylesheet, no
      // preconnect to someone else's CDN on the critical path.
    ],
    scripts: [
      // Set theme before paint to prevent a flash of the wrong color scheme.
      { children: THEME_INIT_SCRIPT },
      { type: "application/ld+json", children: JSON.stringify(organizationJsonLd()) },
      { type: "application/ld+json", children: JSON.stringify(websiteJsonLd()) },
      ...analyticsScripts,
      ...(SERVICE_WORKER_SCRIPT ? [{ children: SERVICE_WORKER_SCRIPT }] : []),
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <SiteFooter />
      <PrintPrep />
    </div>
  );
}
